/**
 * Client for the workstation hardware service's label printing capability.
 *
 * The backend runs centrally and cannot reach a printer attached to an operator's
 * counter, so the browser is the transport: it fetches ready-to-spool jobs from
 * the backend and posts each one to the local service on `127.0.0.1`.
 *
 * All rendering happens server-side — see
 * `documentation/legacy_delphi/label_printing_audit.md` §8. Nothing here knows what a
 * label looks like.
 *
 * Pure apart from the injected `fetch`, so every branch is unit-testable.
 */

/**
 * Loopback origin the hardware service listens on when nothing overrides it.
 *
 * The port is part of the published contract: the page has no way to discover it, so
 * it must match the service's configured `LocalApi:Port`. 41960 avoids DYMO Connect's
 * 41951.
 */
export const DEFAULT_HARDWARE_SERVICE_ORIGIN = 'http://127.0.0.1:41960';

/**
 * Resolve the address of the local service.
 *
 * Overridable so a deployment that moved the port does not need a rebuilt bundle, and
 * so tests can point at a stub. A blank or whitespace override falls back rather than
 * producing a URL of `"/v1/health"` against the page's own origin — which would fail
 * in a way that reads as "the service is broken" instead of "this is misconfigured".
 *
 * @param configured Raw value of `VITE_HARDWARE_SERVICE_ORIGIN`, if set.
 */
export function resolveHardwareServiceOrigin(configured?: string): string {
    const trimmed = configured?.trim();

    return trimmed === undefined || trimmed === ''
        ? DEFAULT_HARDWARE_SERVICE_ORIGIN
        : stripTrailingSlashes(trimmed);
}

/**
 * Remove every trailing `/` from an origin.
 *
 * **Why this is not a regex.** This was `trimmed.replace(/\/+$/, '')`, which SonarQube
 * flagged as `typescript:S5852`. `\/+$` backtracks the same way any `X+$` does: for a
 * value with a long run of slashes that does not end in one, the engine restarts `\/+`
 * at each position in the run, consumes to the end, fails `$`, and gives back one
 * character at a time — O(N²). `'http://h' + '/'.repeat(100_000) + 'x'` demonstrates it.
 *
 * The input is `VITE_HARDWARE_SERVICE_ORIGIN`, a deployment-set build variable, so this
 * is a far less exposed path than the barcode one. It is fixed on the same terms anyway:
 * the function is exported and unit-tested directly, "a build variable is trusted" is an
 * assumption about the deployment rather than about the code, and a linear scan is no
 * harder to read than the regex it replaces.
 *
 * Walking backwards over the character codes is exact and single-pass. `47` is `/`.
 */
function stripTrailingSlashes(origin: string): string {
    let end = origin.length;

    while (end > 0 && origin.charCodeAt(end - 1) === 47) {
        end--;
    }

    return origin.slice(0, end);
}

/**
 * The address every call in this module and in `eidReader.ts` is built from.
 *
 * One constant on purpose: `127.0.0.1:41960` must not be spelled out at each call
 * site, or moving the port becomes a search-and-replace across the application.
 */
export const HARDWARE_SERVICE_ORIGIN = resolveHardwareServiceOrigin(
    import.meta.env.VITE_HARDWARE_SERVICE_ORIGIN,
);

/** Identity string the service reports. A process answering on the port is not enough. */
export const HARDWARE_SERVICE_IDENTITY = 'rexo-pms-hardware-service';

/** Local API contract version this build speaks. */
export const HARDWARE_SERVICE_API_VERSION = '1';

/** Capability id for Zebra label printing. */
export const CAPABILITY_PRINT_ZEBRA = 'print.zebra';

/** Capability id for DYMO label printing. */
export const CAPABILITY_PRINT_DYMO = 'print.dymo';

/**
 * Which local endpoint each payload format is posted to.
 *
 * The two are separate routes rather than one with a format field because they do
 * different work: the Zebra route spools bytes the backend already rendered, while
 * the DYMO route rasterises a canonical label document at 300 dpi first. Keeping
 * them apart means a malformed document cannot reach the Zebra path at all.
 */
const ENDPOINT_BY_FORMAT: Record<LabelPayloadFormat, string> = {
    zpl: '/v1/print/zebra',
    'dymo-label': '/v1/print/dymo',
};

/** Which capability the service must advertise to serve each format. */
const CAPABILITY_BY_FORMAT: Record<LabelPayloadFormat, string> = {
    zpl: CAPABILITY_PRINT_ZEBRA,
    'dymo-label': CAPABILITY_PRINT_DYMO,
};

/**
 * Body of the service's `/v1/discovery` — the only endpoint an unpaired page may read.
 *
 * Deliberately minimal. Before pairing, the workstation has no reason to tell a page
 * which hardware is attached to it, so the richer `/v1/health` stays behind the origin
 * allowlist and this is what detection runs on.
 */
export interface HardwareServiceDiscovery {
    service: string;
    apiVersion: string;
    paired: boolean;
}

/** Body of the service's `/v1/health`. Readable only from a paired or configured origin. */
export interface HardwareServiceHealth {
    service: string;
    serviceVersion: string;
    apiVersion: string;
    platform: string;
    operatingSystem: string;
    architecture: string;
    paired: boolean;
    /**
     * Stable id for this installation. Sent to the backend so it can derive which
     * cash point — and therefore which printer queue — this workstation belongs to.
     * Not a credential: on its own it authorises nothing.
     */
    installationId: string;
    capabilities: string[];
    /**
     * Whether the national eID middleware can serve a card read.
     *
     * Typed `unknown` on purpose: it arrives from a service that may be older than this page, so
     * it is validated field by field by `domain/eidMiddleware` rather than trusted as a shape.
     * Absent on any service that predates the readiness probe, which must keep working unchanged.
     */
    eidMiddleware?: unknown;
}

/**
 * Payload formats the workstation service accepts.
 *
 * `zpl` is raw Zebra command text. `dymo-label` is a canonical label document as
 * JSON, which the service rasterises and sends to a LabelWriter as native raster
 * commands — no DYMO software is installed on the workstation.
 */
export type LabelPayloadFormat = 'zpl' | 'dymo-label';

/** One ready-to-spool job, as the backend builds it. */
export interface LabelPrintJob {
    queue: string;
    payload: string;
    format: LabelPayloadFormat;
    idempotencyKey: string;
    objectLetter: string;
    /**
     * The device the workstation service resolved, echoed back so it can confirm the job
     * is meant for the printer it approved. Never a way to choose a different one.
     */
    printerId?: string | null;
}

/** One label printer the workstation service detected. */
export interface DetectedPrinter {
    id: string;
    provider: LabelPrinterProvider;
    displayName: string;
    model?: string | null;
    serialNumber?: string | null;
    transport: string;
    installed: boolean;
    connected: boolean;
    ready: boolean;
    selected: boolean;
    statusReason?: string | null;
}

/** Which family a detected printer belongs to. */
export type LabelPrinterProvider = 'zebra' | 'dymo';

/**
 * How the service arrived at the effective printer.
 *
 * `automatic-deterministic` means several were ready and none chosen, so the service picked one by
 * a stable rule. `failover` means the chosen one stopped being usable and another took over — the
 * only source that warrants telling the operator something happened.
 */
export type PrinterSelectionSource =
    | 'automatic-single'
    | 'automatic-deterministic'
    | 'persisted'
    | 'failover'
    | 'none';

/** Body of the service's `/v1/printers`. */
export interface PrinterSelectionState {
    printers: DetectedPrinter[];
    selectedPrinterId: string | null;
    selectedProvider: LabelPrinterProvider | null;
    selectionSource: PrinterSelectionSource;
    selectionRequired: boolean;
    ready: boolean;
    statusReason?: string | null;
    code?: string | null;
    allowAutomaticFailover: boolean;
    observedAt: string;
    /**
     * The printer this workstation would rather use, when it is not the effective one.
     *
     * Preferred and effective are separate facts and both screens show both. A preference is kept
     * while its printer is unavailable, so an operator sees that their choice still stands rather
     * than being told nothing is configured.
     */
    preferredPrinterId?: string | null;
    /** Display name of the printer a failover just moved away from, if any. */
    failedOverFrom?: string | null;
}

/** What the service detection concluded. */
export type HardwareServiceStatus =
    /** Nothing answered on the loopback port. */
    | { state: 'unavailable' }
    /** Something answered but is not this service — never trusted on port alone. */
    | { state: 'unrecognised' }
    /** This service, but speaking a different API contract version. */
    | { state: 'incompatible'; apiVersion: string }
    /**
     * This service, speaking our contract, but this workstation has not been connected
     * to the application yet. Nothing about the attached hardware is known, because an
     * unpaired service does not report it.
     */
    | { state: 'unpaired' }
    /** Reachable, paired, and speaking our contract. */
    | { state: 'ready'; health: HardwareServiceHealth };

/** Outcome of one job. */
export interface LabelJobResult {
    objectLetter: string;
    outcome: 'printed' | 'duplicate' | 'failed';
    /** Stable error code when the outcome is `failed`. */
    errorCode?: string;
    /**
     * The family this job must be rebuilt for, when failover crossed a provider boundary.
     *
     * Present only with `PRINTER_PROVIDER_CHANGED`, which the service raises *before* submitting
     * anything. Nothing has reached the spooler, so rebuilding and retrying cannot duplicate a
     * label — and rebuilding is mandatory, because a payload encoded for one family must never be
     * sent to the other.
     */
    renderForProvider?: LabelPrinterProvider;
}

/** Outcome of a whole print run. */
export interface LabelPrintSummary {
    results: LabelJobResult[];
    printed: number;
    duplicates: number;
    failed: number;
    /** Letters that reached the printer, for an operator-facing message. */
    printedLetters: string[];
}

/** Minimal shape of the injected fetch, so tests need no DOM. */
export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

/**
 * Ask the local service what it is, and how far it can be used.
 *
 * Two calls, because the service splits what it will tell anyone from what it will
 * only tell a connected application:
 *
 * 1. `/v1/discovery` is readable from any origin. It answers "is this our service,
 *    does it speak our contract, and has this workstation been connected yet".
 * 2. `/v1/health` carries the installation id, the capability list and the attached
 *    printers, and is refused until this origin is paired. It is only attempted once
 *    discovery says pairing is done.
 *
 * A process answering on the expected port is **not** trusted on that basis: the
 * identity string and API version are both checked, because any local application
 * could bind the port first.
 */
export async function probeHardwareService(fetchImpl: FetchLike): Promise<HardwareServiceStatus> {
    let response: Response;

    try {
        response = await fetchImpl(`${HARDWARE_SERVICE_ORIGIN}/v1/discovery`, { method: 'GET' });
    } catch {
        // A connection refusal is the normal "not installed" case, not an error worth
        // surfacing as a fault.
        return { state: 'unavailable' };
    }

    if (!response.ok) {
        return { state: 'unavailable' };
    }

    let discovery: HardwareServiceDiscovery;

    try {
        discovery = (await response.json()) as HardwareServiceDiscovery;
    } catch {
        return { state: 'unrecognised' };
    }

    if (discovery?.service !== HARDWARE_SERVICE_IDENTITY) {
        return { state: 'unrecognised' };
    }

    if (discovery.apiVersion !== HARDWARE_SERVICE_API_VERSION) {
        return { state: 'incompatible', apiVersion: discovery.apiVersion };
    }

    if (!discovery.paired) {
        return { state: 'unpaired' };
    }

    return await loadHealth(fetchImpl);
}

/**
 * Read the full health report from a service that has already said it is paired.
 *
 * A refusal here means the workstation is paired with a *different* origin — the
 * service is connected, but not to this address — which is reported as unpaired so the
 * operator is offered the one action that fixes it.
 */
async function loadHealth(fetchImpl: FetchLike): Promise<HardwareServiceStatus> {
    try {
        const response = await fetchImpl(`${HARDWARE_SERVICE_ORIGIN}/v1/health`, { method: 'GET' });

        if (!response.ok) {
            return { state: 'unpaired' };
        }

        const health = (await response.json()) as HardwareServiceHealth;

        return health?.service === HARDWARE_SERVICE_IDENTITY
            ? { state: 'ready', health }
            : { state: 'unrecognised' };
    } catch {
        return { state: 'unpaired' };
    }
}

/** Whether a ready service advertises a given capability. */
function advertises(status: HardwareServiceStatus, capability: string): boolean {
    return status.state === 'ready' && status.health.capabilities.includes(capability);
}

/** Whether a ready service advertises Zebra printing. */
export function canPrintZebra(status: HardwareServiceStatus): boolean {
    return advertises(status, CAPABILITY_PRINT_ZEBRA);
}

/** Whether a ready service advertises DYMO printing. */
export function canPrintDymo(status: HardwareServiceStatus): boolean {
    return advertises(status, CAPABILITY_PRINT_DYMO);
}

/**
 * Whether a ready service can print labels of any family.
 *
 * Used to gate the operator-facing button, which is offered before the cashpoint's
 * printer family is known — the backend resolves that from the pairing binding when
 * the jobs are fetched. A job whose format the service cannot serve is refused at
 * the point of printing, with its own code, rather than by hiding the button.
 */
export function canPrintLabels(status: HardwareServiceStatus): boolean {
    return canPrintZebra(status) || canPrintDymo(status);
}

/**
 * Whether this workstation still needs connecting before it will do any hardware work.
 *
 * Also true when the service is paired with a different origin: from this page's point
 * of view the outcome is the same, and the fix is the same one action.
 */
export function needsPairing(status: HardwareServiceStatus): boolean {
    return status.state === 'unpaired';
}

/**
 * Whether pairing is worth offering right now.
 *
 * False for an unreachable, unrecognised or version-incompatible service: pairing
 * cannot succeed against any of them, and offering a button that always fails is worse
 * than saying what is actually wrong. Guards the caller against re-running the
 * handshake on a workstation that is already connected.
 */
export function canPair(status: HardwareServiceStatus | null): boolean {
    return status !== null && status.state === 'unpaired';
}

/**
 * Whether an error code means this workstation has to be paired again.
 *
 * A pairing has two sides and they can disagree. The local service keeps its own record, which an
 * upgrade or a repair deliberately leaves alone; the backend keeps a row per installation, which a
 * restored database does not. When the backend's half is gone the service still answers every
 * probe as a paired, healthy workstation — correctly, about itself — so
 * {@link probeHardwareService} cannot notice, {@link canPair} stays false, and the operator is
 * told the workstation is not paired with no button offered to fix it.
 *
 * Observed on 2026-09-23: `hardware_service_installation` was empty while the service reported
 * itself paired, and recovering needed an administrator to delete the service's state file and
 * restart it. No operator would find that. These codes are the backend's half of the answer and
 * the only evidence the page ever gets, so they are what puts the button back.
 */
export function pairingLostFromErrorCode(code: string | undefined): boolean {
    return code === 'INSTALLATION_NOT_PAIRED' || code === 'INSTALLATION_REVOKED';
}

/** What the service returned from <c>POST /v1/pair</c>. */
export interface PairProof {
    installationId: string;
    publicKey: string;
    proof: string;
}

/**
 * Hand a backend-issued challenge to the local service and collect its proof.
 *
 * Step 2 of the handshake. The page never inspects the challenge or the proof — it
 * only relays them — so it holds no key material and cannot forge either side.
 */
export async function submitPairingChallenge(
    fetchImpl: FetchLike,
    challenge: string,
): Promise<PairProof | null> {
    let response: Response;

    try {
        response = await fetchImpl(`${HARDWARE_SERVICE_ORIGIN}/v1/pair`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ challenge }),
        });
    } catch {
        return null;
    }

    if (!response.ok) {
        return null;
    }

    try {
        const body = (await response.json()) as Partial<PairProof>;

        if (!body?.installationId || !body.publicKey || !body.proof) {
            return null;
        }

        return {
            installationId: body.installationId,
            publicKey: body.publicKey,
            proof: body.proof,
        };
    } catch {
        return null;
    }
}

/**
 * Ask the service which label printer this workstation uses.
 *
 * The provider comes from here, not from a guess: only the workstation can see the
 * device attached to it, and the family is read off that device's identity rather
 * than off a queue name in a shop-wide configuration row.
 */
export async function loadPrinterSelection(
    fetchImpl: FetchLike,
    options: { refresh?: boolean } = {},
): Promise<PrinterSelectionState | null> {
    const path = options.refresh === true ? '/v1/printers/refresh' : '/v1/printers';

    try {
        const response = await fetchImpl(`${HARDWARE_SERVICE_ORIGIN}${path}`, {
            method: options.refresh === true ? 'POST' : 'GET',
            headers: { 'Content-Type': 'application/json' },
        });

        return response.ok ? ((await response.json()) as PrinterSelectionState) : null;
    } catch {
        return null;
    }
}

/**
 * Save which printer this workstation uses.
 *
 * Once. The service resolves it for every later job, so an operator does not choose
 * again for each label. Passing null clears the choice and returns the workstation to
 * automatic resolution.
 */
export async function selectPrinter(
    fetchImpl: FetchLike,
    printerId: string | null,
): Promise<PrinterSelectionState | null> {
    try {
        const response = await fetchImpl(`${HARDWARE_SERVICE_ORIGIN}/v1/printers/select`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ printerId }),
        });

        return response.ok ? ((await response.json()) as PrinterSelectionState) : null;
    } catch {
        return null;
    }
}

/**
 * Post one job to the service, on the route its format belongs to.
 *
 * A job whose format this service does not advertise is refused here rather than
 * sent: the local endpoint would answer 404, which reads as "the service is broken"
 * instead of "this build cannot drive that printer family".
 */
export async function printLabelJob(
    fetchImpl: FetchLike,
    job: LabelPrintJob,
    status?: HardwareServiceStatus,
): Promise<LabelJobResult> {
    const endpoint = ENDPOINT_BY_FORMAT[job.format];

    if (endpoint === undefined) {
        return {
            objectLetter: job.objectLetter,
            outcome: 'failed',
            errorCode: 'UNSUPPORTED_FORMAT',
        };
    }

    if (status !== undefined && !advertises(status, CAPABILITY_BY_FORMAT[job.format])) {
        return {
            objectLetter: job.objectLetter,
            outcome: 'failed',
            errorCode: 'CAPABILITY_MISSING',
        };
    }

    let response: Response;

    try {
        response = await fetchImpl(`${HARDWARE_SERVICE_ORIGIN}${endpoint}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                queue: job.queue,
                payload: job.payload,
                format: job.format,
                idempotencyKey: job.idempotencyKey,
                // A confirmation, not a selection: the service refuses anything other than the
                // device it resolved for this workstation.
                printerId: job.printerId ?? null,
            }),
        });
    } catch {
        return {
            objectLetter: job.objectLetter,
            outcome: 'failed',
            errorCode: 'SERVICE_UNREACHABLE',
        };
    }

    if (!response.ok) {
        let errorCode = 'PRINT_FAILED';
        let renderForProvider: LabelPrinterProvider | undefined;

        try {
            const body = (await response.json()) as {
                error?: string;
                renderForProvider?: string;
            };
            errorCode = body?.error ?? errorCode;

            if (body?.renderForProvider === 'zebra' || body?.renderForProvider === 'dymo') {
                renderForProvider = body.renderForProvider;
            }
        } catch {
            // A non-JSON error body is still a failure; the default code stands.
        }

        return { objectLetter: job.objectLetter, outcome: 'failed', errorCode, renderForProvider };
    }

    const body = (await response.json()) as { printed?: boolean; duplicate?: boolean };

    return {
        objectLetter: job.objectLetter,
        outcome: body?.duplicate === true ? 'duplicate' : 'printed',
    };
}

/**
 * Post every job, in order, stopping at the first failure.
 *
 * Sequential because the service serialises operations per device anyway, and
 * because the letter order is what an operator sees coming out of the printer.
 *
 * **Stops on the first failure by design.** Continuing would leave a partial run
 * that cannot be cleanly retried: a retry fetches new idempotency keys, so the
 * labels that already printed would print a second time. Stopping early keeps the
 * common failure — printer offline or out of media — a clean no-op.
 */
export async function printLabelJobs(
    fetchImpl: FetchLike,
    jobs: readonly LabelPrintJob[],
    status?: HardwareServiceStatus,
): Promise<LabelPrintSummary> {
    const results: LabelJobResult[] = [];

    for (const job of jobs) {
        const result = await printLabelJob(fetchImpl, job, status);
        results.push(result);

        if (result.outcome === 'failed') {
            break;
        }
    }

    return summarisePrintResults(results);
}

/** What the backend answered when asked for a contract's jobs. */
export type LabelJobsResult = { ok: true; jobs: LabelPrintJob[] } | { ok: false; code?: string };

/**
 * Loads a contract's print jobs from the backend.
 *
 * Takes the provider so the payload can be rebuilt for a different printer family after a
 * failover. Callers that never need that may ignore the argument.
 */
export type LoadLabelJobs = (provider?: LabelPrinterProvider) => Promise<LabelJobsResult>;

/** Outcome of printing a whole contract's labels. */
export type ContractLabelOutcome =
    /** The backend could not build the jobs. */
    | { kind: 'build-failed'; code?: string }
    /** The contract has no pledged objects, so there is nothing to label. */
    | { kind: 'no-objects' }
    /** Jobs were sent; inspect the summary for what actually printed. */
    | {
          kind: 'completed';
          summary: LabelPrintSummary;
          /**
           * The printer the run moved to mid-way, when a failover crossed a provider boundary.
           *
           * Set so the page can raise the toast that makes automatic failover acceptable: the
           * operator is told which family the remaining labels came out on.
           */
          failedOverToProvider?: LabelPrinterProvider;
      };

/**
 * Fetch a contract's jobs from the backend and print them.
 *
 * Shared by both triggers the legacy has (audit §2): automatically at contract
 * creation, and manually from the reprint form. Implementing only the reprint
 * would silently drop the automatic label, which is the primary path in daily use.
 */
export async function printContractLabels(
    loadJobs: LoadLabelJobs,
    fetchImpl: FetchLike,
    status?: HardwareServiceStatus,
): Promise<ContractLabelOutcome> {
    const loaded = await loadJobs();

    if (!loaded.ok) {
        return { kind: 'build-failed', code: loaded.code };
    }

    if (loaded.jobs.length === 0) {
        return { kind: 'no-objects' };
    }

    const summary = await printLabelJobs(fetchImpl, loaded.jobs, status);
    // `results[length - 1]` rather than `.at(-1)`: this project's TypeScript lib target predates
    // `Array.prototype.at`, and the run stops at its first failure, so the last entry is it.
    const failure = summary.results[summary.results.length - 1];

    // The one automatic retry, and only for the one case where it is provably safe: the service
    // raises this *before* handing anything to the spooler, so no label can already have come out.
    if (
        failure?.errorCode !== 'PRINTER_PROVIDER_CHANGED' ||
        failure.renderForProvider === undefined
    ) {
        return { kind: 'completed', summary };
    }

    const provider = failure.renderForProvider;
    const rebuilt = await loadJobs(provider);

    if (!rebuilt.ok) {
        return { kind: 'completed', summary };
    }

    // Only the labels that have not printed. Reprinting the whole contract would give the
    // already-printed objects a second physical label, because a rebuild issues fresh idempotency
    // keys and the service's duplicate suppression cannot recognise them.
    const printed = new Set(summary.printedLetters);
    const remaining = rebuilt.jobs.filter(job => !printed.has(job.objectLetter));

    if (remaining.length === 0) {
        return { kind: 'completed', summary };
    }

    const retry = await printLabelJobs(fetchImpl, remaining, status);

    return {
        kind: 'completed',
        // The failed attempt is dropped from the record in favour of what the replacement did with
        // the same letter: it never reached a printer, and keeping it would report one object as
        // both failed and printed.
        summary: summarisePrintResults([
            ...summary.results.filter(result => result.outcome !== 'failed'),
            ...retry.results,
        ]),
        failedOverToProvider: provider,
    };
}

/** Reduce job results to the counts an operator-facing message needs. */
export function summarisePrintResults(results: readonly LabelJobResult[]): LabelPrintSummary {
    const printedLetters = results
        .filter(result => result.outcome === 'printed')
        .map(result => result.objectLetter);

    return {
        results: [...results],
        printed: printedLetters.length,
        duplicates: results.filter(result => result.outcome === 'duplicate').length,
        failed: results.filter(result => result.outcome === 'failed').length,
        printedLetters,
    };
}
